import { onBeforeUnmount, Ref } from "@vue/composition-api";

// height of the strip at the top and at the bottom of the visible list where a dragged item scrolls the list
const ZONE = 80;
// scroll step per animation frame at the very edge, smaller farther from it
const MAX_STEP = 20;

const findScroller = (el: HTMLElement | null): HTMLElement => {
  let node = el?.parentElement ?? null;
  while (node) {
    const { overflowY } = getComputedStyle(node);
    if (/(auto|scroll)/.test(overflowY) && node.scrollHeight > node.clientHeight) return node;
    node = node.parentElement;
  }
  return (document.scrollingElement as HTMLElement) ?? document.documentElement;
};

const pointerY = (e: TouchEvent | MouseEvent | DragEvent): number | null => {
  if ("touches" in e) return e.touches[0]?.clientY ?? null;
  // native drag ends with dragover at 0,0 in some browsers
  return e.clientX === 0 && e.clientY === 0 ? null : e.clientY;
};

// Sortable scrolls only within 30 px of the scroll container edge, and at the bottom of the reorder list the edge is
// under the sticky Reorder/Cancel buttons: a file could not be dragged far up or down a long list (CO-15592).
// While an item is dragged, the list scrolls when the finger or the mouse is near the top of the visible list or near
// the buttons, on touch, mouse and native drag.
export default function useDragAutoScroll(root: Ref<HTMLElement | null>, bottomBar: Ref<HTMLElement | null>) {
  let scroller: HTMLElement | null = null;
  let y: number | null = null;
  let frame = 0;

  const visibleArea = (el: HTMLElement) => {
    const isPage = el === document.scrollingElement || el === document.documentElement;
    const rect = isPage ? { top: 0, bottom: window.innerHeight } : el.getBoundingClientRect();
    const barTop = bottomBar.value?.getBoundingClientRect().top ?? rect.bottom;
    return { top: Math.max(rect.top, 0), bottom: Math.min(rect.bottom, barTop, window.innerHeight) };
  };

  const step = () => {
    frame = 0;
    if (y === null || !scroller) return;
    const { top, bottom } = visibleArea(scroller);
    let delta = 0;
    if (y < top + ZONE) delta = -Math.min(MAX_STEP, Math.ceil(((top + ZONE - y) / ZONE) * MAX_STEP));
    else if (y > bottom - ZONE) delta = Math.min(MAX_STEP, Math.ceil(((y - bottom + ZONE) / ZONE) * MAX_STEP));
    if (!delta) return;
    const before = scroller.scrollTop;
    scroller.scrollTop = before + delta;
    // keep scrolling while the pointer stays in the strip and the list can still move
    if (scroller.scrollTop !== before) frame = requestAnimationFrame(step);
  };

  const onMove = (e: TouchEvent | MouseEvent | DragEvent) => {
    y = pointerY(e);
    if (y !== null && !frame) frame = requestAnimationFrame(step);
  };

  const stop = () => {
    document.removeEventListener("touchmove", onMove);
    document.removeEventListener("mousemove", onMove);
    document.removeEventListener("dragover", onMove);
    if (frame) cancelAnimationFrame(frame);
    frame = 0;
    y = null;
    scroller = null;
  };

  const start = () => {
    stop();
    scroller = findScroller(root.value);
    document.addEventListener("touchmove", onMove, { passive: true });
    document.addEventListener("mousemove", onMove, { passive: true });
    document.addEventListener("dragover", onMove, { passive: true });
  };

  onBeforeUnmount(stop);

  return { start, stop };
}
